import { Animal, Ubicacion, Unidad, Empresa, AnimalHistorico, infoCompraVenta } from "../types/interfaces";
import { TypesUnion } from "../types/types";
import { Enums } from "../types/enums";
import { createDate, formatStringToLocaleDateTime } from '../utils/dateHelper';

/**
 * Genera un UUID v4 de forma segura en cualquier contexto (incluido HTTP plano,
 * donde crypto.randomUUID() no está disponible).
 */
const safeUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Animal model with helper methods
export class AnimalModel implements Animal {
  id?: string; // Optional ID for database records
  fecha_de_creacion: string;
  caravana: string;
  updated_at?: string; // Remote sync field - matches Supabase animales table
  sexo: TypesUnion["SEXO"] | undefined;
  peso: number | undefined;
  ubicacion_id: string | null | undefined;
  ubicacion_nombre?: string; // Name of the location (joined from ubicaciones)
  raza: TypesUnion["RAZA"] | undefined;
  observaciones?: string | undefined;
  categoria: TypesUnion["CATEGORIA"] | undefined;
  tiene_ternero: boolean | undefined;
  edad: TypesUnion["TIPO_EDAD"] | undefined;
  empresa_id: string;
  prenies: TypesUnion["TIPO_PRENIEZ"] | undefined;
  fecha_de_evento: string;
  estado: TypesUnion["ESTADO"] | undefined;
  caravanas_viejas: string[] | undefined;
  peso_inicial?: number;
  es_comprado?: boolean;
  fecha_nacimiento?: string; // Estimated birth date for SENASA declarations

  // Additional tracking fields
  fecha_ultimo_evento_sexo?: string;
  fecha_ultimo_evento_peso?: string;
  fecha_ultimo_evento_ubicacion_id?: string;
  fecha_ultimo_evento_raza?: string;
  fecha_ultimo_evento_observaciones?: string;
  fecha_ultimo_evento_prenies?: string;
  fecha_ultimo_evento_edad?: string;
  fecha_ultimo_evento_estado?: string;
  fecha_ultimo_evento_tiene_ternero?: string;
  fecha_ultimo_evento_categoria?: string;
  fecha_ultimo_evento_caravanas_viejas?: string;
  fecha_ultimo_evento_es_comprado?: string;
  fecha_ultimo_evento_renspa?: string;
  fecha_ultima_modificacion_sexo?: string;
  fecha_ultima_modificacion_peso?: string;
  fecha_ultima_modificacion_ubicacion_id?: string;
  fecha_ultima_modificacion_raza?: string;
  fecha_ultima_modificacion_observaciones?: string;
  fecha_ultima_modificacion_prenies?: string;
  fecha_ultima_modificacion_edad?: string;
  fecha_ultima_modificacion_estado?: string;
  fecha_ultima_modificacion_tiene_ternero?: string;
  fecha_ultima_modificacion_categoria?: string;
  fecha_ultima_modificacion_caravanas_viejas?: string;
  fecha_ultima_modificacion_es_comprado?: string;
  fecha_ultima_modificacion_renspa?: string;
  fecha_de_evento_peso_inicial?: string;
  fecha_modificacion_peso_inicial?: string;
  pesajes?: Array<{
    peso: number;
    fecha_evento: string;
    fecha_creacion: string;
  }>;
  prenies_historial?: Array<{
    prenies: string | null;
    fecha_evento: string;
    tiene_ternero?: boolean;
    fecha_creacion: string;
  }>;
  ubicaciones_historial?: Array<{
    ubicacion_id: string;
    fecha_evento: string;
    fecha_creacion: string;
  }>;

  operacion_venta_id?: string;
  operacion_compra_id?: string;
  info_compra?: infoCompraVenta;
  info_venta?: infoCompraVenta;
  session_code?: string; // Código único de sesión de carga
  renspa?: string; // RENSPA (Registro Nacional Sanitario de Productores Bovinos)
  madre?: string; // Caravana de la madre del animal (parentesco opcional)
  padre?: string; // Caravana del padre del animal (parentesco opcional)

  constructor(data: Partial<Animal>) {
    this.id = data.id;
    this.fecha_de_creacion = data.fecha_de_creacion || new Date().toISOString();
    this.caravana = data.caravana || "";
    this.updated_at = data.updated_at;
    this.sexo = data.sexo;
    this.peso = data.peso;
    this.ubicacion_id = data.ubicacion_id;
    this.ubicacion_nombre = (data as any).ubicacion_nombre;
    this.raza = data.raza;
    this.observaciones = data.observaciones;
    this.categoria = data.categoria;
    this.tiene_ternero = data.tiene_ternero;
    this.edad = data.edad;
    this.prenies = data.prenies;
    this.empresa_id = data.empresa_id || '';
    this.fecha_de_evento = data.fecha_de_evento || createDate();
    this.estado = data.estado;
    this.caravanas_viejas = data.caravanas_viejas;
    this.peso_inicial = data.peso_inicial;
    this.es_comprado = data.es_comprado;
    this.fecha_nacimiento = data.fecha_nacimiento;

    // Additional tracking fields
    this.fecha_ultimo_evento_sexo = data.fecha_ultimo_evento_sexo;
    this.fecha_ultimo_evento_peso = data.fecha_ultimo_evento_peso;
    this.fecha_ultimo_evento_ubicacion_id = data.fecha_ultimo_evento_ubicacion_id;
    this.fecha_ultimo_evento_raza = data.fecha_ultimo_evento_raza;
    this.fecha_ultimo_evento_observaciones = data.fecha_ultimo_evento_observaciones;
    this.fecha_ultimo_evento_prenies = data.fecha_ultimo_evento_prenies;
    this.fecha_ultimo_evento_edad = data.fecha_ultimo_evento_edad;
    this.fecha_ultimo_evento_estado = data.fecha_ultimo_evento_estado;
    this.fecha_ultimo_evento_tiene_ternero = data.fecha_ultimo_evento_tiene_ternero;
    this.fecha_ultimo_evento_categoria = data.fecha_ultimo_evento_categoria;
    this.fecha_ultimo_evento_caravanas_viejas = data.fecha_ultimo_evento_caravanas_viejas;
    this.fecha_ultimo_evento_es_comprado = data.fecha_ultimo_evento_es_comprado;
    this.fecha_ultimo_evento_renspa = data.fecha_ultimo_evento_renspa;
    this.fecha_ultima_modificacion_sexo = data.fecha_ultima_modificacion_sexo;
    this.fecha_ultima_modificacion_peso = data.fecha_ultima_modificacion_peso;
    this.fecha_ultima_modificacion_ubicacion_id = data.fecha_ultima_modificacion_ubicacion_id;
    this.fecha_ultima_modificacion_raza = data.fecha_ultima_modificacion_raza;
    this.fecha_ultima_modificacion_observaciones = data.fecha_ultima_modificacion_observaciones;
    this.fecha_ultima_modificacion_prenies = data.fecha_ultima_modificacion_prenies;
    this.fecha_ultima_modificacion_edad = data.fecha_ultima_modificacion_edad;
    this.fecha_ultima_modificacion_estado = data.fecha_ultima_modificacion_estado;
    this.fecha_ultima_modificacion_tiene_ternero = data.fecha_ultima_modificacion_tiene_ternero;
    this.fecha_ultima_modificacion_categoria = data.fecha_ultima_modificacion_categoria;
    this.fecha_ultima_modificacion_caravanas_viejas = data.fecha_ultima_modificacion_caravanas_viejas;
    this.fecha_ultima_modificacion_es_comprado = data.fecha_ultima_modificacion_es_comprado;
    this.fecha_ultima_modificacion_renspa = data.fecha_ultima_modificacion_renspa;
    this.fecha_de_evento_peso_inicial = data.fecha_de_evento_peso_inicial;
    this.fecha_modificacion_peso_inicial = data.fecha_modificacion_peso_inicial;
    this.pesajes = data.pesajes;
    this.prenies_historial = data.prenies_historial;
    this.ubicaciones_historial = data.ubicaciones_historial;
    this.operacion_venta_id = data.operacion_venta_id;
    this.operacion_compra_id = data.operacion_compra_id;
    this.info_compra = data.info_compra;
    this.info_venta = data.info_venta;
    this.session_code = data.session_code;
    this.renspa = data.renspa;
    this.madre = data.madre;
    this.padre = data.padre;
  }

  // Get animal estado display name
  getEstadoDisplay(): string | undefined {
    return this.estado;
  }

  // Get animal age display name
  getAgeDisplay(): string {
    return this.edad
      ? `Tipo de Edad: ${this.edad}`
      : "Tipo de Edad: No especificado";
  }

  // Get pregnancy estado display name
  // Sin dato (null/undefined) ≠ VACIA: no se cargó preñez para el animal (KAN-185)
  getPregnancyEstadoDisplay(): string {
    if (this.prenies == null || this.prenies === '') return "Sin dato";
    return this.prenies !== "VACIA" ? "Preñada" : "No preñada";
  }

  // Get calf estado display name
  getCalfEstadoDisplay(): string {
    return this.tiene_ternero ? "Con ternero" : "Sin ternero";
  }

  // Get category display name
  getCategoryDisplay(): string {
    return `Categoría: ${this.categoria}`;
  }

  // Check if animal is active
  isActive(): boolean {
    return this.estado === Enums.ESTADO.ACTIVO;
  }

  // Get the last update date from all modification dates
  getLastUpdateDate(): string | null {
    const modificationDates = [
      this.fecha_ultima_modificacion_sexo,
      this.fecha_ultima_modificacion_peso,
      this.fecha_ultima_modificacion_ubicacion_id,
      this.fecha_ultima_modificacion_raza,
      this.fecha_ultima_modificacion_observaciones,
      this.fecha_ultima_modificacion_prenies,
      this.fecha_ultima_modificacion_edad,
      this.fecha_ultima_modificacion_estado,
      this.fecha_ultima_modificacion_tiene_ternero,
      this.fecha_ultima_modificacion_categoria,
      this.fecha_ultima_modificacion_caravanas_viejas,
      // KAN-200: es_comprado y renspa también se marcan como modificación al
      // editarse (syncAnimalesLocal → checkUpdate), pero no figuraban acá: la
      // columna "Actualización" y su orden ignoraban esas ediciones.
      this.fecha_ultima_modificacion_es_comprado,
      this.fecha_ultima_modificacion_renspa
    ].filter(date => date); // Remove null/undefined values

    if (modificationDates.length === 0) {
      return null; // No modifications recorded
    }

    // Find the most recent date
    const sortedDates = modificationDates.sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime());
    return sortedDates[0] || null;
  }

  // Get formatted last update date for display
  getFormattedLastUpdateDate(): string {
    const lastUpdate = this.getLastUpdateDate();
    if (!lastUpdate) {
      return 'Nunca actualizado';
    }
    return formatStringToLocaleDateTime(lastUpdate);
  }

  // Get color status for last update date display
  getLastUpdateDateColor(): string {
    const lastUpdate = this.getLastUpdateDate();
    if (!lastUpdate) {
      return 'text-danger'; // Never updated = red
    }

    const lastUpdateDate = new Date(lastUpdate);
    const now = new Date();
    const monthsDiff = (now.getTime() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44); // Approximate months

    // Check if this is an active calf (ternero activo)
    const isActiveCalf = this.categoria === Enums.CATEGORIA.TERNERO && this.estado === Enums.ESTADO.ACTIVO;

    if (isActiveCalf) {
      // Active calves: yellow after 4 months, red after 1 year
      if (monthsDiff > 12) {
        return 'text-danger';
      } else if (monthsDiff > 4) {
        return 'text-warning';
      }
    } else {
      // All other animals: yellow after 1 year, red after 2 years
      if (monthsDiff > 24) {
        return 'text-danger';
      } else if (monthsDiff > 12) {
        return 'text-warning';
      }
    }

    return 'text-success'; // Green color for recent updates
  }
}

// Ubicacion model with helper methods
export class UbicacionModel implements Ubicacion {
  id: string;
  created_at: string;
  updated_at?: string;
  nombre: string;
  descripcion?: string;
  unidad_id: string;
  hectareas?: number;
  tipo: TypesUnion["UBICACION_TIPO"];
  empresa_id?: string;

  constructor(data: Partial<Ubicacion>) {
    // crypto.randomUUID() no existe en contextos no seguros (HTTP plano, no localhost).
    // Si falta el id, usamos un fallback seguro para no romper la carga de la lista.
    this.id = data.id || safeUUID();
    this.created_at = data.created_at || new Date().toISOString();
    this.updated_at = data.updated_at;
    this.nombre = data.nombre || "";
    this.descripcion = data.descripcion;
    this.unidad_id = data.unidad_id || '';
    this.hectareas = data.hectareas;
    this.tipo = data.tipo || Enums.UBICACION_TIPO.CUADRO;
    this.empresa_id = data.empresa_id;
  }

  // Ubicacion model with helper methods - simplified for new schema
}

// Unidad model with helper methods
export class UnidadModel implements Unidad {
  id: string;
  created_at: string;
  updated_at?: string;
  nombre: string;
  descripcion?: string;
  empresa_id: string;
  hectareas_total?: number;

  constructor(data: Partial<Unidad>) {
   this.id = data.id || '';
   this.created_at = data.created_at || new Date().toISOString();
   this.updated_at = data.updated_at;
   this.nombre = data.nombre || "";
   this.descripcion = data.descripcion;
   this.empresa_id = data.empresa_id || '';
   this.hectareas_total = data.hectareas_total;
 }

  // Get display name for the unidad
  getDisplayName(): string {
    return this.nombre;
  }

  // Get total hectares display
  getTotalHectaresDisplay(): string {
    return this.hectareas_total ? `${this.hectareas_total} ha` : "N/A";
  }

  /**
   * Hectáreas totales del campo con lógica híbrida:
   * - Si el campo tiene `hectareas_total` cargado (> 0), usa ese valor manual.
   * - Si no, suma las hectáreas de sus ubicaciones hijas.
   * Así se alinea lo que muestra el formulario (campo manual) con la lista y el detalle.
   */
  getTotalHectareas(ubicaciones: { hectareas?: number | null }[] = []): number {
    if (this.hectareas_total && this.hectareas_total > 0) {
      return Math.round(this.hectareas_total * 10) / 10;
    }
    const sum = ubicaciones.reduce((s, u) => s + (u.hectareas || 0), 0);
    return Math.round(sum * 10) / 10;
  }
}

// Empresa model with helper methods
export class EmpresaModel implements Empresa {
  id: string;
  created_at: string;
  nombre: string;
  descripcion?: string;

  constructor(data: Partial<Empresa>) {
    this.id = data.id || '';
    this.created_at = data.created_at || new Date().toISOString();
    this.nombre = data.nombre || "";
    this.descripcion = data.descripcion;
  }

  // Get display name for the empresa
  getDisplayName(): string {
    return this.nombre;
  }
}

// Factory functions to create models from plain objects
export const createAnimal = (data: Partial<AnimalHistorico>): AnimalModel =>
  new AnimalModel(data);
export const createUbicacion = (data: Partial<Ubicacion>): UbicacionModel =>
  new UbicacionModel(data);
export const createUnidad = (data: Partial<Unidad>): UnidadModel =>
  new UnidadModel(data);
export const createEmpresa = (data: Partial<Empresa>): EmpresaModel =>
  new EmpresaModel(data);

// Validation functions
export const validateAnimalData = (
  animal: Partial<Animal>
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!animal.caravana) errors.push("Caravana is required");
  if (!animal.sexo) errors.push("Sexo is required");
  if (animal.peso !== undefined && animal.peso <= 0) errors.push("Peso must be greater than 0");
  if (!animal.categoria) errors.push("Categoria is required");
  if (!animal.estado) errors.push("Estado is required");
  // prenies es opcional: una vaca sin tacto es válida (KAN-185)

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateUbicacionData = (
  ubicacion: Partial<Ubicacion>
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!ubicacion.nombre) errors.push("Nombre is required");
  if (!ubicacion.unidad_id) errors.push("Unidad ID is required");
  if (!ubicacion.tipo) errors.push("Tipo is required");

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateUnidadData = (
  unidad: Partial<Unidad>
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!unidad.nombre) errors.push("Nombre is required");
  //if (!unidad.empresa_id || unidad.empresa_id === 0) errors.push('Empresa ID is required');

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateEmpresaData = (
  empresa: Partial<Empresa>
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!empresa.nombre) errors.push("Nombre is required");

  return {
    isValid: errors.length === 0,
    errors,
  };
};
